import React, { useState, useRef, useEffect } from 'react';
import { FiInfo, FiX, FiClock, FiUser } from 'react-icons/fi';

const HoldInfoPopover = ({ holdReason, holdDate, holdQuantity, totalQuantity, holdBy, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target) && 
          triggerRef.current && !triggerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const togglePopover = (e) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      return new Date(dateString).toLocaleString();
    } catch (error) {
      return 'Unknown';
    }
  };

  return (
    <div className={`relative ${className}`}>
      {/* Trigger Button */}
      <button
        ref={triggerRef}
        onClick={togglePopover}
        className="w-4 h-4 text-red-500 hover:text-red-700 cursor-pointer transition-colors duration-200"
        title="Click to view hold details"
      >
        <FiInfo className="w-4 h-4" />
      </button>

      {/* Popover */}
      {isOpen && (
        <div
          ref={popoverRef}
          className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
        >
          {/* Arrow pointing down */}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-200"></div>
          
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-gray-100 bg-red-50 rounded-t-lg">
            <div className="flex items-center">
              <FiInfo className="w-4 h-4 text-red-600 mr-2" />
              <h3 className="text-sm font-semibold text-red-800">Hold Information</h3>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
            >
              <FiX className="w-4 h-4" />
            </button>
          </div>

          {/* Content */}
          <div className="p-3 space-y-3">
            {/* Quantity Info */}
            {holdQuantity && totalQuantity && (
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Quantity</p>
                <p className="text-sm text-gray-900">
                  <span className="font-semibold text-red-600">{holdQuantity}</span> of {totalQuantity} units on hold
                </p>
              </div>
            )}

            {/* Hold Reason */}
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Reason</p>
              <div className="mt-1 p-2 bg-gray-50 rounded-md">
                <p className="text-sm text-gray-800 leading-relaxed">
                  {holdReason || 'No reason provided'}
                </p>
              </div>
            </div>

            {/* Hold Date */}
            {holdDate && (
              <div className="flex items-center text-xs text-gray-500">
                <FiClock className="w-3 h-3 mr-1" />
                <span>Held on {formatDate(holdDate)}</span>
              </div>
            )}

            {/* Hold By */}
            {holdBy && (
              <div className="flex items-center text-xs text-gray-500">
                <FiUser className="w-3 h-3 mr-1" />
                <span>Held by {holdBy}</span>
              </div>
            )}
          </div>

          {/* Footer Note */}
          <div className="px-3 py-2 bg-gray-50 rounded-b-lg border-t border-gray-100">
            <p className="text-xs text-gray-500">
              Contact warehouse management to resolve this hold
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default HoldInfoPopover; 